import { formatPhoneNumber } from '@packages/corekit/src/utils/phoneNumbers';

export function convertToMinutesSeconds(decimalMinutes: number) {
  const wholeMinutes = Math.floor(decimalMinutes);
  const seconds = Math.round((decimalMinutes - wholeMinutes) * 60);
  const formattedSeconds = seconds.toString().padStart(2, '0');

  return `${wholeMinutes}:${formattedSeconds}`;
}

/**
 * Title-case a team member's display name. Backend names often arrive in the
 * French HR convention with all-caps last names (e.g. "George TURP" or
 * "JEAN-PIERRE LACROIX"). Normalising at the display layer keeps the inbox
 * preview readable without depending on a backend cleanup.
 *
 * Handles word boundaries on spaces, hyphens, and apostrophes; preserves any
 * Unicode letter set so accented characters round-trip cleanly.
 */
export function formatMemberName(name: string | undefined | null): string {
  if (!name) {
    return '';
  }
  return name
    .toLocaleLowerCase()
    .replace(/(^|[\s\-'’])(\p{L})/gu, (_, separator, char) => separator + char.toLocaleUpperCase());
}

export function getNameFromContact(contact?: {
  label?: string;
  name?: string;
  last_name?: string;
}) {
  if (contact === undefined) {
    return '';
  }

  const { name, last_name: lastName, label } = contact;

  if (!name && !lastName && label) {
    return label;
  }

  return lastName ? `${name || ''} ${lastName}`.trim() : name || '';
}

export function getNameOrNumberFromContact(contact?: {
  label?: string;
  name?: string;
  last_name?: string;
  numbers?: string[];
}) {
  const name = getNameFromContact(contact);

  const number = contact?.numbers?.[0]
    ? formatPhoneNumber(contact.numbers[0], { format: 'national' }).number
    : '';

  return name || number || '';
}

export function getInitials(name: string) {
  if (!name) {
    return undefined;
  }

  // Split by words, not characters
  const words = name.split(/\s+/).filter((word) => word.length > 0);

  if (words.length === 1 && words[0]?.startsWith('+')) {
    return undefined;
  }

  // Filter out words that are only emojis
  const wordsWithoutEmojis = words.filter((word) => {
    // Check if the entire word consists only of emojis
    const characters = Array.from(word); // Properly handle Unicode characters
    return !characters.every((char) => isEmoji(char));
  });

  // Get first character of each word (up to 2 words)
  return wordsWithoutEmojis
    .slice(0, 2)
    .map((word) => Array.from(word)[0]) // Use Array.from to properly handle Unicode
    .join('')
    .toUpperCase();
}

// TODO: there must be a smarter way to do this
function isEmoji(char: string): boolean {
  const codePoint = char.codePointAt(0);
  if (!codePoint) {
    return false;
  }

  return (
    // Basic Emoji Ranges
    (codePoint >= 0x1f600 && codePoint <= 0x1f64f) || // Emoticons
    (codePoint >= 0x1f300 && codePoint <= 0x1f5ff) || // Miscellaneous Symbols and Pictographs
    (codePoint >= 0x1f680 && codePoint <= 0x1f6ff) || // Transport and Map Symbols
    (codePoint >= 0x1f1e0 && codePoint <= 0x1f1ff) || // Regional Indicator Symbols (flags)
    (codePoint >= 0x1f900 && codePoint <= 0x1f9ff) || // Supplemental Symbols and Pictographs
    // Extended Emoji Ranges
    (codePoint >= 0x1fa70 && codePoint <= 0x1faff) || // Symbols and Pictographs Extended-A
    (codePoint >= 0x1f000 && codePoint <= 0x1f02f) || // Mahjong Tiles
    (codePoint >= 0x1f030 && codePoint <= 0x1f09f) || // Domino Tiles
    (codePoint >= 0x1f0a0 && codePoint <= 0x1f0ff) || // Playing Cards
    (codePoint >= 0x1f100 && codePoint <= 0x1f1ff) || // Enclosed Alphanumeric Supplement
    (codePoint >= 0x1f200 && codePoint <= 0x1f2ff) || // Enclosed Ideographic Supplement
    (codePoint >= 0x1f700 && codePoint <= 0x1f77f) || // Alchemical Symbols
    (codePoint >= 0x1f780 && codePoint <= 0x1f7ff) || // Geometric Shapes Extended
    (codePoint >= 0x1f800 && codePoint <= 0x1f8ff) || // Supplemental Arrows-C
    // Symbol Ranges
    (codePoint >= 0x2600 && codePoint <= 0x26ff) || // Miscellaneous Symbols
    (codePoint >= 0x2700 && codePoint <= 0x27bf) || // Dingbats
    (codePoint >= 0x2b00 && codePoint <= 0x2bff) || // Miscellaneous Symbols and Arrows
    (codePoint >= 0x3030 && codePoint <= 0x303f) || // CJK Symbols and Punctuation (wavy dash)
    (codePoint >= 0x1f170 && codePoint <= 0x1f251) || // Enclosed Alphanumeric Supplement
    // Geometric and Mathematical Symbols
    (codePoint >= 0x25a0 && codePoint <= 0x25ff) || // Geometric Shapes
    (codePoint >= 0x2190 && codePoint <= 0x21ff) || // Arrows
    (codePoint >= 0x2000 && codePoint <= 0x206f) || // General Punctuation
    (codePoint >= 0x2070 && codePoint <= 0x209f) || // Superscripts and Subscripts
    (codePoint >= 0x20a0 && codePoint <= 0x20cf) || // Currency Symbols
    (codePoint >= 0x2100 && codePoint <= 0x214f) || // Letterlike Symbols
    (codePoint >= 0x2150 && codePoint <= 0x218f) || // Number Forms
    (codePoint >= 0x2460 && codePoint <= 0x24ff) || // Enclosed Alphanumerics
    (codePoint >= 0x2500 && codePoint <= 0x257f) || // Box Drawing
    (codePoint >= 0x2580 && codePoint <= 0x259f) || // Block Elements
    // Technical and Miscellaneous
    (codePoint >= 0x238c && codePoint <= 0x2454) || // Miscellaneous Technical
    (codePoint >= 0x2300 && codePoint <= 0x23ff) || // Miscellaneous Technical
    (codePoint >= 0x1f650 && codePoint <= 0x1f67f) || // Ornamental Dingbats
    (codePoint >= 0x1f910 && codePoint <= 0x1f96b) || // Additional emoticons
    (codePoint >= 0x1f980 && codePoint <= 0x1f997) || // Additional symbols and pictographs
    // Combining and Modifier Symbols
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) || // Variation Selectors
    (codePoint >= 0x20d0 && codePoint <= 0x20ff) || // Combining Diacritical Marks for Symbols
    (codePoint >= 0x1f3fb && codePoint <= 0x1f3ff) || // Emoji Skin Tone Modifiers
    // Additional Common Emoji Characters
    codePoint === 0x00a9 || // Copyright sign ©
    codePoint === 0x00ae || // Registered sign ®
    codePoint === 0x203c || // Double exclamation mark ‼
    codePoint === 0x2049 || // Exclamation question mark ⁉
    codePoint === 0x2122 || // Trade mark sign ™
    codePoint === 0x2139 || // Information source ℹ
    codePoint === 0x2194 || // Left right arrow ↔
    codePoint === 0x2195 || // Up down arrow ↕
    codePoint === 0x2196 || // North west arrow ↖
    codePoint === 0x2197 || // North east arrow ↗
    codePoint === 0x2198 || // South east arrow ↘
    codePoint === 0x2199 || // South west arrow ↙
    codePoint === 0x21a9 || // Leftwards arrow with hook ↩
    codePoint === 0x21aa || // Rightwards arrow with hook ↪
    codePoint === 0x231a || // Watch ⌚
    codePoint === 0x231b || // Hourglass ⌛
    codePoint === 0x2328 || // Keyboard ⌨
    codePoint === 0x23cf || // Eject symbol ⏏
    codePoint === 0x23e9 || // Black right-pointing double triangle ⏩
    codePoint === 0x23ea || // Black left-pointing double triangle ⏪
    codePoint === 0x23eb || // Black up-pointing double triangle ⏫
    codePoint === 0x23ec || // Black down-pointing double triangle ⏬
    codePoint === 0x23ed || // Black right-pointing double triangle with vertical bar ⏭
    codePoint === 0x23ee || // Black left-pointing double triangle with vertical bar ⏮
    codePoint === 0x23ef || // Black right-pointing triangle with double vertical bar ⏯
    codePoint === 0x23f0 || // Alarm clock ⏰
    codePoint === 0x23f1 || // Stopwatch ⏱
    codePoint === 0x23f2 || // Timer clock ⏲
    codePoint === 0x23f3 || // Hourglass with flowing sand ⏳
    codePoint === 0x23f8 || // Double vertical bar ⏸
    codePoint === 0x23f9 || // Black square for stop ⏹
    codePoint === 0x23fa || // Black circle for record ⏺
    codePoint === 0x24c2 || // Circled latin capital letter m Ⓜ
    codePoint === 0x25aa || // Black small square ▪
    codePoint === 0x25ab || // White small square ▫
    codePoint === 0x25b6 || // Black right-pointing triangle ▶
    codePoint === 0x25c0 || // Black left-pointing triangle ◀
    codePoint === 0x25fb || // White medium square ◻
    codePoint === 0x25fc || // Black medium square ◼
    codePoint === 0x25fd || // White medium small square ◽
    codePoint === 0x25fe || // Black medium small square ◾
    codePoint === 0x2934 || // Arrow pointing rightwards then curving upwards ⤴
    codePoint === 0x2935 || // Arrow pointing rightwards then curving downwards ⤵
    codePoint === 0x2b05 || // Leftwards black arrow ⬅
    codePoint === 0x2b06 || // Upwards black arrow ⬆
    codePoint === 0x2b07 || // Downwards black arrow ⬇
    codePoint === 0x2b1b || // Black large square ⬛
    codePoint === 0x2b1c || // White large square ⬜
    codePoint === 0x2b50 || // White medium star ⭐
    codePoint === 0x2b55 || // Heavy large circle ⭕
    codePoint === 0x3297 || // Circled ideograph congratulation ㊗
    codePoint === 0x3299 // Circled ideograph secret ㊙
  );
}
